// src/contexts/ColorThemeContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";

// Color theme type matching nexbii-platform-idea
export type ColorTheme = {
  name: string;
  primary: string;
  secondary: string;
  accent: string;
};

// Define color themes exactly like nexbii-platform-idea
export const colorThemes: ColorTheme[] = [
  {
    name: "purple",
    primary: "from-purple-600 to-pink-600",
    secondary: "text-purple-600",
    accent: "bg-purple-600 hover:bg-purple-700",
  },
  {
    name: "blue",
    primary: "from-blue-600 to-indigo-600",
    secondary: "text-blue-600",
    accent: "bg-blue-600 hover:bg-blue-700",
  },
  {
    name: "green",
    primary: "from-emerald-600 to-teal-600",
    secondary: "text-emerald-600",
    accent: "bg-emerald-600 hover:bg-emerald-700",
  },
  {
    name: "amber",
    primary: "from-amber-500 to-orange-600",
    secondary: "text-amber-600",
    accent: "bg-amber-600 hover:bg-amber-700",
  },
  {
    name: "rose",
    primary: "from-rose-600 to-red-600",
    secondary: "text-rose-600",
    accent: "bg-rose-600 hover:bg-rose-700",
  },
];

type ColorThemeContextType = {
  theme: ColorTheme;
  setTheme: (theme: ColorTheme) => void;
};

const ColorThemeContext = createContext<ColorThemeContextType | undefined>(undefined);

export function ColorThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<ColorTheme>(colorThemes[0]);
  const [mounted, setMounted] = useState(false);

  // Load saved theme from localStorage
  useEffect(() => {
    setMounted(true);
    const savedTheme = localStorage.getItem("color-theme");
    if (savedTheme) {
      const foundTheme = colorThemes.find((t) => t.name === savedTheme);
      if (foundTheme) {
        setTheme(foundTheme);
      }
    }
  }, []);

  // Save theme to localStorage when it changes
  useEffect(() => {
    if (mounted) {
      localStorage.setItem("color-theme", theme.name);
    }
  }, [theme, mounted]);

  return (
    <ColorThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ColorThemeContext.Provider>
  );
}

export function useColorTheme() {
  const context = useContext(ColorThemeContext);
  if (context === undefined) {
    throw new Error("useColorTheme must be used within a ColorThemeProvider");
  }
  return context;
}